import React, { useEffect, useMemo, useState } from 'react';

import Heading from '@/components/atoms/Heading';
import usePresentCampaign from '@/hooks/logic/campaign/usePresentCampaign';
import {
  PresentCampaignInfo,
  PresentCampaign,
} from '@/types/api/mypage/campaign/presentCampaignInfo';
import LinkButton from '@/components/atoms/LinkButton';

/**
 * プレゼントキャンペーンの「応募状況」に表示するラベルを取得する。
 * @param status キャンペーンステータス
 * @returns 表示名
 */
const getPresentCampaignButtonLabel = (status: string): string => {
  switch (status) {
    case 'EXPIRED':
      return '応募終了';
    case 'APPLIED':
      return '応募済み';
    default:
      return '応募する';
  }
};

const getPresentCampaignButtonComponent = (campaign: PresentCampaign): React.JSX.Element => {
  const label = getPresentCampaignButtonLabel(campaign.present_status);
  if (campaign.present_status === 'APPLY') {
    return (
      <LinkButton
        variant='secondary'
        href={{
          pathname: '/campaign/present',
          query: { code: campaign.present_code },
        }}
      >
        {label}
      </LinkButton>
    );
  }
  return <div>{label}</div>;
};

/**
 * 「特典キャンペーン」のメニュー画面に表示する「プレゼントキャンペーン」のコンポーネント。
 *
 * @return コンポーネント
 */
const MenuPresentCampaign = () => {
  const { getPresentCampaignInfo } = usePresentCampaign();

  const [presentCampaignInfo, setPresentCampaignInfo] = useState<PresentCampaignInfo>();

  const storePresentCampaignInfo = async () => {
    const response = await getPresentCampaignInfo();
    if (response) {
      setPresentCampaignInfo(response);
    }
  };

  /**
   * 初期処理を実施する。
   */
  useEffect((): void => {
    storePresentCampaignInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPresentCampaignInfoRows = useMemo((): React.JSX.Element[] | null => {
    if (!presentCampaignInfo) {
      return null;
    }
    const rows = presentCampaignInfo.present_campaigns?.map((e) => {
      return (
        <tr key={e.present_code}>
          <th className='m-table__th'>{e.present_name}</th>
          <td className='m-table__td'>{getPresentCampaignButtonComponent(e)}</td>
        </tr>
      );
    });
    return rows;
  }, [presentCampaignInfo]);

  return (
    <div className='m-box-02'>
      <div className='m-box-02__inner'>
        <Heading as='h2' icon='present'>
          プレゼントキャンペーン情報
        </Heading>
        <div className='m-table'>
          <table>
            <colgroup>
              <col className='w-60' />
              <col className='w-40' />
            </colgroup>
            <thead>
              <tr>
                <th className='m-table__th'>プレゼントキャンペーン名称</th>
                <th className='m-table__th'>応募状況</th>
              </tr>
            </thead>
            <tbody>{getPresentCampaignInfoRows}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MenuPresentCampaign;
